import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { DiscoveryEpisode, DiscoverySeasonSummary } from "@rago/web-shared";
import {
  discoverySource,
  toEpisodeMediaItem,
  toMediaItem,
  toSeasonMediaItem,
} from "../../sources/discoverySource";
import { BackCrumb } from "../../components/library/back-crumb";
import { useAmbientBackdrop } from "../../lib/pageBackdrop";
import { CastRow } from "../../components/library/cast-row";
import { DetailHero } from "../../components/library/detail-hero";
import { EpisodeSheet } from "../../components/library/episode-sheet";
import { EpisodesGrid } from "../../components/library/season-browser";
import { LoadingState, NotFoundState } from "../../components/page-state";

export const Route = createFileRoute("/_authed/discover/tv/$tmdbId/season/$seasonNumber")({
  component: DiscoverySeasonPage,
});

/**
 * The Discovery season page — the library season page's composition over
 * TMDB: the SHOW title leads (linked back to the show), "Season N" beneath,
 * the season overview (the series overview when TMDB has none), then the
 * 16:9 episode grid whose cards open the episode sheet. The show detail
 * rides the show page's query key, so arriving from there is warm.
 */
function DiscoverySeasonPage() {
  // An art route: the shell's backdrop carries the last art seen while
  // nothing of ours has decoded (lib/pageBackdrop) — first hook, before any
  // early return, so the spinner phase has it too.
  useAmbientBackdrop();
  const { tmdbId, seasonNumber } = Route.useParams();
  const n = Number.parseInt(seasonNumber, 10);
  const valid = /^\d+$/.test(tmdbId) && Number.isInteger(n) && n >= 0;
  const [open, setOpen] = useState<DiscoveryEpisode | null>(null);

  const detail = useQuery({
    queryKey: ["discover-detail", "tv", tmdbId],
    queryFn: () => discoverySource.getDetail("tv", tmdbId),
    staleTime: 6 * 60 * 60 * 1000,
    enabled: valid,
  });
  const season = useQuery({
    queryKey: ["discover-season", tmdbId, n],
    queryFn: () => discoverySource.getSeason(tmdbId, n),
    staleTime: 6 * 60 * 60 * 1000,
    enabled: valid,
  });

  if (!valid) return <NotFoundState backTo="/discover" backLabel="Back to Discover" />;
  if (detail.isLoading) return <LoadingState />;
  if (detail.isError || !detail.data) {
    return <NotFoundState backTo="/discover" backLabel="Back to Discover" />;
  }

  const d = detail.data;
  // The detail payload drops specials and episode-less seasons; a deep link
  // to one still renders, from the season response's own name.
  const summary: DiscoverySeasonSummary = d.seasons.find((s) => s.seasonNumber === n) ?? {
    seasonNumber: n,
    name: season.data?.name ?? `Season ${n}`,
    episodeCount: season.data?.episodes.length ?? 0,
    posterPath: null,
    airYear: null,
    overview: "",
  };
  const episodes = (season.data?.episodes ?? []).map((ep) =>
    toEpisodeMediaItem(ep, { tvId: tmdbId, seasonNumber: n, showTitle: d.title }),
  );
  const showItem = toMediaItem(d);

  return (
    <>
      <DetailHero
        item={toSeasonMediaItem(summary, tmdbId)}
        source={discoverySource}
        backdropUrl={discoverySource.imageUrl(showItem, "backdrop", 1920)}
        remember={{ sourceId: discoverySource.id, item: showItem }}
        kicker={<BackCrumb />}
        title={
          <Link
            to="/discover/$mediaType/$tmdbId"
            params={{ mediaType: "tv", tmdbId }}
            className="text-[0.8em] no-underline hover:underline decoration-2 underline-offset-8"
          >
            {d.title}
          </Link>
        }
        subtitle={`Season ${n}`}
        summary={summary.overview || d.overview || undefined}
        below={
          <div className="rago-detail-below">
            <div className="px-6 sm:px-10">
              {season.isLoading ? (
                <LoadingState minH="min-h-[20vh]" />
              ) : episodes.length > 0 ? (
                <EpisodesGrid
                  source={discoverySource}
                  episodes={episodes}
                  onSelect={(item) =>
                    setOpen(
                      season.data?.episodes.find((e) => e.episodeNumber === item.index) ?? null,
                    )
                  }
                />
              ) : (
                <p className="mb-6 text-sm text-[var(--rago-text-3)]">No episodes listed.</p>
              )}
            </div>
            <CastRow detail={d} />
          </div>
        }
      />
      <EpisodeSheet episode={open} show={d} seasonNumber={n} onClose={() => setOpen(null)} />
    </>
  );
}
